// splitPreview.ts — a client-side PREVIEW of the payer/patient split shown
// on the cashless discharge screen (wireframe S5), before the real split is
// computed and written server-side. Insurance Claims Phase 4.5.5.
//
// Why this exists rather than calling the server for a preview: there is no
// pre-discharge split-preview endpoint (backend/utils/claimSplit.js's
// splitStatement() needs each line's resolved invoice id, which only exists
// once the room-rent/charges lines actually become bill_items — i.e. at the
// moment discharge mints the IP invoice. A true line-level preview can't
// exist before that moment). What CAN be computed exactly ahead of time,
// from just the running bill's net and the claim's final authorisation, is
// the TOTAL insurer/patient split — see splitStatement's own lines 137-139,
// which this mirrors precisely:
//   insurerShare = min(approvedAmount, statementNet)
//   patientShare = statementNet - insurerShare
// That total is what actually matters for the discharge screen's "Collect
// from patient" panel and the server's echo-amount guard.
//
// The HEAD-WISE breakdown shown in the table is a best-effort estimate only
// (deductions applied by head, any remaining gap spread proportionally
// across still-payable heads) — it will not always match the final
// per-invoice split (that depends on which bill/invoice a line lands on,
// not just its head), so it is presented as a preview, not a promise. The
// row TOTALS always reconcile to the exact numbers above.
export const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

export interface RunningBillHeadPreview {
  head: string;
  net: number;
}

export interface SplitDeduction {
  head?: string;
  amount: number;
}

export interface SplitPreviewHead {
  head: string;
  net: number;
  payerAmount: number;
  patientAmount: number;
}

export interface SplitPreviewResult {
  statementNet: number;
  insurerShare: number;
  patientShare: number;
  heads: SplitPreviewHead[];
}

export function computeSplitPreview(
  heads: RunningBillHeadPreview[],
  approvedAmount: number | null | undefined,
  deductions: SplitDeduction[] = []
): SplitPreviewResult {
  const statementNet = round2(heads.reduce((s, h) => s + (Number(h.net) || 0), 0));
  const insurerShare = round2(Math.max(0, Math.min(Number(approvedAmount) || 0, statementNet)));
  const patientShare = round2(statementNet - insurerShare);

  // Layer 2 (estimate): itemised deductions reduce their own head's payable
  // amount first, capped at that head's net.
  const working = heads.map((h) => ({ head: h.head, net: round2(Number(h.net) || 0), patientAmount: 0 }));
  for (const d of deductions) {
    const requested = round2(Number(d.amount) || 0);
    if (requested <= 0) continue;
    const line = working.find((h) => h.head === d.head);
    if (!line) continue; // untargeted/unknown head — folded into the residual below instead
    const capacity = round2(line.net - line.patientAmount);
    const take = Math.min(capacity, requested);
    line.patientAmount = round2(line.patientAmount + take);
  }

  // Layer 3 (estimate): whatever gap remains between the itemised layer and
  // the insurer's actual approved total, spread proportionally across every
  // head's still-payable capacity — an estimate of the real bill-type
  // priority order, close enough for a preview table.
  const tentativeInsurerTotal = round2(working.reduce((s, h) => s + (h.net - h.patientAmount), 0));
  const rawResidual = round2(tentativeInsurerTotal - insurerShare);
  if (Math.abs(rawResidual) >= 0.01) {
    const direction = rawResidual > 0 ? 1 : -1;
    let remaining = Math.abs(rawResidual);
    const capacityOf = (h: { net: number; patientAmount: number }) =>
      direction > 0 ? round2(h.net - h.patientAmount) : h.patientAmount;
    const totalCapacity = round2(working.reduce((s, h) => s + capacityOf(h), 0));
    if (totalCapacity > 0) {
      for (const h of working) {
        if (remaining <= 0) break;
        const cap = capacityOf(h);
        if (cap <= 0) continue;
        const share = round2(Math.min(cap, remaining * (cap / totalCapacity)));
        h.patientAmount = round2(h.patientAmount + direction * share);
        remaining = round2(remaining - share);
      }
    }
  }

  return {
    statementNet, insurerShare, patientShare,
    heads: working.map((h) => ({
      head: h.head, net: h.net,
      patientAmount: round2(Math.max(0, Math.min(h.net, h.patientAmount))),
      payerAmount: round2(h.net - Math.max(0, Math.min(h.net, h.patientAmount))),
    })),
  };
}
